import React from 'react'
import { Link } from 'react-router-dom'
import type { RecentOrder } from '../../types/partnerDashboard'

interface OrdersMiniTableProps {
  orders: RecentOrder[]
  accentColor: string
}

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  EN_ATTENTE:       { label: 'En attente',       color: '#92400e', bg: '#fef3c7' },
  ACCEPTEE:         { label: 'Acceptée',         color: '#065f46', bg: '#d1fae5' },
  REFUSEE:          { label: 'Refusée',          color: '#991b1b', bg: '#fee2e2' },
  EN_COURS:         { label: 'En cours',         color: '#1e40af', bg: '#dbeafe' },
  PRET_A_RECUPERER: { label: 'Prêt à récupérer', color: '#6d28d9', bg: '#ede9fe' },
  EN_LIVRAISON:     { label: 'En livraison',     color: '#0369a1', bg: '#e0f2fe' },
  LIVREE:           { label: 'Livrée',           color: '#166534', bg: '#dcfce7' },
  CLOTUREE:         { label: 'Clôturée',         color: '#374151', bg: '#f3f4f6' },
  ANNULEE:          { label: 'Annulée',          color: '#991b1b', bg: '#fee2e2' },
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount)

/**
 * Mini-tableau des 10 dernières commandes partenaire.
 * Responsive : bascule en liste de cartes sous 768px (via media query inline simplifiée).
 */
const OrdersMiniTable: React.FC<OrdersMiniTableProps> = ({ orders, accentColor }) => {
  if (orders.length === 0) {
    return (
      <p data-testid="no-orders" style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>
        Aucune commande récente
      </p>
    )
  }

  return (
    <>
      {/* Version desktop — tableau */}
      <div data-testid="orders-table" className="orders-table-desktop">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              {['Date', 'Statut', 'Client', 'Total', 'Coupon', ''].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '0.6rem 0.5rem',
                    color: '#64748b',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const st = STATUS_LABELS[order.status] ?? { label: order.status, color: '#374151', bg: '#f3f4f6' }
              return (
                <tr key={order.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.6rem 0.5rem', color: '#1e293b' }}>{formatDate(order.created_at)}</td>
                  <td style={{ padding: '0.6rem 0.5rem' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.78rem',
                        fontWeight: '500',
                        color: st.color,
                        background: st.bg,
                      }}
                    >
                      {st.label}
                    </span>
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', color: '#1e293b' }}>{order.client_name}</td>
                  <td style={{ padding: '0.6rem 0.5rem', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(order.total_price)}</td>
                  <td style={{ padding: '0.6rem 0.5rem' }}>
                    {order.coupon_code ? (
                      <span style={{ fontFamily: 'monospace', color: accentColor, fontWeight: '600', fontSize: '0.85rem' }}>
                        {order.coupon_code}
                      </span>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem' }}>
                    <Link
                      to={`/partner/orders`}
                      style={{ color: accentColor, textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem' }}
                    >
                      Voir
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Version mobile — cartes */}
      <div className="orders-cards-mobile" data-testid="orders-cards">
        {orders.map((order) => {
          const st = STATUS_LABELS[order.status] ?? { label: order.status, color: '#374151', bg: '#f3f4f6' }
          return (
            <div
              key={order.id}
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '0.5rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{formatDate(order.created_at)}</span>
                <span
                  style={{
                    padding: '0.15rem 0.45rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    color: st.color,
                    background: st.bg,
                  }}
                >
                  {st.label}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>{formatCurrency(order.total_price)}</span>
                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{order.client_name}</span>
              </div>
              {order.coupon_code && (
                <p style={{ margin: '0.25rem 0 0 0', fontFamily: 'monospace', fontSize: '0.8rem', color: accentColor }}>
                  Coupon: {order.coupon_code}
                </p>
              )}
            </div>
          )
        })}
      </div>

      {/* CSS responsive inline */}
      <style>{`
        .orders-cards-mobile { display: none; }
        @media (max-width: 768px) {
          .orders-table-desktop { display: none !important; }
          .orders-cards-mobile { display: block !important; }
        }
      `}</style>
    </>
  )
}

export default OrdersMiniTable
